import React, { createContext, useState, useCallback, useEffect, useContext } from 'react';
import useOrders from '../hooks/useOrders';
import useMealOptions from '../hooks/useMealOptions';
import { set } from 'date-fns';
import { BenefitDetailsContext } from '../contexts/BenefitDetailsContext';
import { updateOrder } from '../api/updateOrder';
import { useSnackbar } from 'notistack';
import { putNewAddress } from '../api/putNewAddress';
import { deleteOrder } from '../api/deleteOrder';
import useUserAddress from '../hooks/useUserAddress';

// Default context values
const defaultContextValue = {
  state: {
    orderManagementTab: 0,
    ordersData: null,
    isLoading: false,
    dataFetched: false,
    ordersMetrics: null,
    mappedOrdersData: { history: [], upcoming: [], future: [] },
  },
  actions: {
    setOrderManagementTab: () => {},
    updateOrderMeals: () => {},
    updateMealQuantity: () => {},
    updateOrderAddress: () => {},
    updateNewAddress: () => {},
    adjustTag: () => {},
    deleteThisOrder: () => {},
  },
};

export const OrdersContext = createContext(defaultContextValue);

// Create a component to determine if we should load the full provider
export const ConditionalOrdersProvider = ({ children }) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const [currentPath, setCurrentPath] = useState('');

  useEffect(() => {
    // Function to check if current route needs orders context
    const checkRoute = () => {
      const path = window.location.pathname;
      setCurrentPath(path);

      // Only load for routes that need orders functionality
      // This is a allowed routes approach - ONLY these routes will load the OrdersContext
      const ordersRoutes = [
        '/dashboard',
        '/billing',
        '/account',
        '/orders',
        '/settings',
        // Add any other routes that need the OrdersContext
      ];

      // Check if current route starts with any of the ordersRoutes
      const needsOrders = ordersRoutes.some((route) => path === route || path.startsWith(`${route}`) || path === '/');

      // For debugging - remove in production
      // console.log(`OrdersContext: Path ${path} will ${needsOrders ? '' : 'NOT '}load orders context`);

      setShouldLoad(needsOrders);
    };

    // Check initially
    checkRoute();

    // Listen for route changes
    const handleRouteChange = () => {
      checkRoute();
    };

    window.addEventListener('popstate', handleRouteChange);

    // Also listen for React Router changes by periodically checking the URL
    // This is needed because popstate doesn't fire with programmatic navigation
    const interval = setInterval(() => {
      if (window.location.pathname !== currentPath) {
        checkRoute();
      }
    }, 300);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      clearInterval(interval);
    };
  }, [currentPath]);

  // Use the full provider for routes that need it, otherwise use empty provider
  return shouldLoad ? (
    <OrdersContextProvider>{children}</OrdersContextProvider>
  ) : (
    <OrdersContext.Provider value={defaultContextValue}>{children}</OrdersContext.Provider>
  );
};

export const OrdersContextProvider = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { mainBenefitDetails } = useContext(BenefitDetailsContext);
  const { refetch: refetchUserAddress } = useUserAddress();

  const [orderManagementTab, setOrderManagementTab] = useState(0);
  const { data, isLoading, refetch: refetchOrders } = useOrders();
  const [mealOptionsData, setMealOptionsData] = useState(null);
  const [mainBenefitId, setMainBenefitId] = useState(null);
  const [ordersMetrics, setOrdersMetrics] = useState([]);

  useEffect(() => {
    if (mainBenefitDetails?.benefit_id) {
      setMainBenefitId(mainBenefitDetails.benefit_id);
      // console.log('here is the mainBenefitId in OrdersContextProvider: ', mainBenefitDetails.benefit_id);
    }
  }, [mainBenefitDetails]);

  // console.log('mainBenefitDetails.benefit_id:', mainBenefitDetails?.benefit_id, 'Type:', typeof mainBenefitDetails?.benefit_id);

  // const mealOptions = Number.isInteger(mainBenefitDetails?.benefit_id) ? useMealOptions(mainBenefitDetails.benefit_id)
  // console.log('here is the mainBenefitDetails.benefit_id in OrdersContextProvider: ', Number.isInteger(mainBenefitDetails?.benefit_id));
  // const mealOptions = useMealOptions(48)

  const { data: mealOptions } = useMealOptions(mainBenefitDetails?.benefit_id || 0);

  useEffect(() => {
    // Update meal options data when valid data is available
    if (mainBenefitDetails?.benefit_id && mealOptions) {
      setMealOptionsData(mealOptions);
    }
  }, [mainBenefitDetails, mealOptions]);

  const [ordersData, setOrdersData] = useState(null);
  const [mappedOrdersData, setMappedOrdersData] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);

  // useEffect(() => {
  //     console.log('here is the ordersData in OrdersContextProvider: ', ordersData);
  //     console.log('here is the mealOptionsData in OrdersContextProvider: ', mealOptionsData);

  // }, [ordersData, mealOptionsData]);

  useEffect(() => {
    if (ordersData && mealOptionsData && Array.isArray(mealOptionsData.boxes)) {
      let boxMap = {};
      for (let box of mealOptionsData.boxes) {
        boxMap = {
          ...boxMap,
          [box.box_id]: box?.servings,
        };
      }
      boxMap = {
        ...boxMap,
        totalServingsPerOrder: mealOptionsData?.total_benefit_servings,
      };
      let eachOrderMetrics = {};
      for (let singleOrder of ordersData) {
        eachOrderMetrics = {
          ...eachOrderMetrics,
          [singleOrder?.id]: {
            totalServingsNeeded: boxMap.totalServingsPerOrder,
            items: [],
          },
        };
        for (let orderItem of singleOrder.order_items) {
          eachOrderMetrics[singleOrder?.id].items.push({
            boxId: orderItem.box_id,
            servings: boxMap[orderItem.box_id],
            quantity: orderItem.quantity,
          });
        }
        eachOrderMetrics[singleOrder?.id].totalServingsContained = eachOrderMetrics[singleOrder.id].items.reduce(
          (acc, item) => acc + item?.servings * item.quantity,
          0,
        );
        eachOrderMetrics[singleOrder.id].totalItemsNeeded =
          eachOrderMetrics[singleOrder?.id].totalServingsNeeded / eachOrderMetrics[singleOrder.id].items[0]?.servings;
      }
      setOrdersMetrics(eachOrderMetrics);
    }
  }, [ordersData, mealOptionsData]);

  const sampleFutureOrder = {
    id: 123,
    status: 'FUTURE',
    total: 0,
    placed_at: '2024-09-26T21:18:02.000Z',
    cutoff_time: '2025-01-29T19:00:00.000Z',
    dropoff_time: '2025-02-10T14:00:00.000Z',
    tracking_url: 'https://d15p8tr8p0vffz.cloudfront.net/9542200c62d375d202665d40/800x.png',
    location_id: 164053,
    benefit_id: 71,
    order_items: [
      {
        order_id: 678973,
        quantity: 1,
        store_item_id: 3534,
        box_id: 449,
        box_name: 'Southern Cajun Chicken (Self-Prepared)',
        box_description:
          'Cajun spiced chicken, stewed collard greens, roasted sweet potatoes, and sesasoned yellow rice \tchicken breast, cayenne, corriander, onion powder, dried oregano, white peper, paprika, dry thyme, kosher salt, onions, ground annato seeds, collards, brown rice, sweet potatoes',
        box_type: 'meal-kits',
        box_image: 'https://seed-api-dev.s3.amazonaws.com/box_images//SouthernCajunChickenMealKit.png',
      },
      {
        order_id: 678973,
        quantity: 1,
        store_item_id: 3535,
        box_id: 450,
        box_name: 'Stuffed Pepper Bowl (Self-Prepared)',
        box_description:
          'Black beans, carrots, cayenne pepper, chickpeas, garlic powder, onion powder, paprika, black pepper, white pepper, kosher salt, thyme, zucchini, onion, green bell peppers, olive oil',
        box_type: 'meal-kits',
        box_image: 'https://seed-api-dev.s3.amazonaws.com/box_images//Stuffed%20Pepper%20Bowl.jpg',
      },
      {
        order_id: 678973,
        quantity: 1,
        store_item_id: 3537,
        box_id: 452,
        box_name: 'Arroz Con Pollo (Self-Prepared)',
        box_description:
          'Green peas, brown rice, chicken breast, coriander, cumin, garlic, garlic powder, ground annatto, lemon juice, olive oil, onion, oregano, black pepper, Roma tomato, kosher salt',
        box_type: 'meal-kits',
        box_image: 'https://seed-api-dev.s3.amazonaws.com/box_images//ArrozConPolloMealKit.jpg',
      },
      {
        order_id: 678973,
        quantity: 1,
        store_item_id: 3538,
        box_id: 453,
        box_name: 'Sesame Garlic Chicken (Self-Prepared)',
        box_description: 'Garlic Chicken, Brown Rice and Fresh Vegetables',
        box_type: 'meal-kits',
        box_image: 'https://seed-api-dev.s3.amazonaws.com/box_images//Sesame%20Garlic%20Chicken%20w%20Broccoli.jpg',
      },
      {
        order_id: 678973,
        quantity: 1,
        store_item_id: 3551,
        box_id: 466,
        box_name: 'Overnight Oats (Self-Prepared)',
        box_description: 'Overnight Oats',
        box_type: 'meal-kits',
        box_image: 'https://seed-api-dev.s3.amazonaws.com/box_images//Overnight%20Oats%20MealKit.jpg',
      },
      {
        order_id: 678973,
        quantity: 1,
        store_item_id: 3555,
        box_id: 470,
        box_name: 'Teriyaki Chicken (Self-Prepared)',
        box_description:
          'Brown rice, carrots, radish, cucumber, chicken breast, cornstarch, garlic, ginger, honey, brown sugar, gf soy sauce, olive oil, kosher salt',
        box_type: 'meal-kits',
        box_image: 'https://seed-api-dev.s3.amazonaws.com/box_images//TeriyakiChickenMealKit.jpg',
      },
      {
        order_id: 678973,
        quantity: 1,
        store_item_id: 3556,
        box_id: 471,
        box_name: 'Rice Noodle Stir Fry (Self-Prepared)',
        box_description: 'Rice Noodle Stir Fry',
        box_type: 'meal-kits',
        box_image: 'https://seed-api-dev.s3.amazonaws.com/box_images//TeriyakiChickenMealKit.jpg',
      },
    ],
    location: {
      id: 164053,
      address: '1320 Los Olivos Avenue',
      address_2: 'Apt 19',
      city: 'Baywood-Los Osos',
      state: 'California',
      zip: '93402',
      lat: 35.3130601,
      long: -120.8263173,
      country: 'United States',
      instructions: 'Do not knock on door please',
    },
  };

  //     History (Statuses = Delivered, Delivery Failed, Canceled)
  //     Upcoming (Statuses = Confirmed, Picked Up)
  //     Future (Statues = Up Next, Future)

  const adjustTag = (order) => {
    if (!order || !order.status) {
      console.error('Order or order status is undefined');
      return 'UNKNOWN';
    }

    const statusMap = {
      FUTURE: 'FUTURE',
      NO_HUB: 'FUTURE',
      PLACED: 'PREPARING',
      ASSIGNED: 'PREPARING',
      APPROVED: 'CONFIRMED',
      CONFIRMED: 'CONFIRMED',
      CANCELED: 'CANCELED',
      CANCELLED: 'CANCELED',
      PICKED_UP: 'PICKED UP',
      DELIVERED: 'DELIVERED',
      'DELIVERY FAILED': 'DELIVERY FAILED',
    };

    const internalStatus = order.status.toUpperCase();
    // console.log('currently working with order: ', order, ' and the status will be: ', statusMap[internalStatus] || internalStatus);
    return statusMap[internalStatus] || internalStatus;
  };

  useEffect(() => {
    const tempObj = {
      history: [],
      upcoming: [],
      future: [],
    };
    if (ordersData && Array.isArray(ordersData)) {
      tempObj.history = ordersData.filter(
        (order) =>
          order.status === 'DELIVERED' ||
          order.status === 'DELIVERY_FAILED' ||
          order.status === 'CANCELED' ||
          order.status === 'CANCELLED',
      );
      tempObj.upcoming = ordersData.filter(
        (order) => order.status === 'CONFIRMED' || order.status === 'PICKED_UP' || order.status === 'APPROVED',
      );
      tempObj.future = ordersData.filter(
        (order) =>
          order.status === 'FUTURE' ||
          order.status === 'NO_HUB' ||
          order.status === 'ASSIGNED' ||
          order.status === 'PLACED' ||
          order.status === 'PREPARING',
      );
    }
    setMappedOrdersData(tempObj);
  }, [ordersData]);

  useEffect(() => {
    // console.log('here is the mappedOrdersData in OrdersContextProvider: ', mappedOrdersData);
  }, [mappedOrdersData]);

  // console.log('here is the info from getorders: ', data);
  // console.log('here is the info from getmealoptions: ', meal_options_data);

  useEffect(() => {
    if (data && Array.isArray(data.orders)) {
      // const tempArr = [sampleFutureOrder, ...data.orders];
      // setOrdersData(tempArr);
      setOrdersData(data.orders);
      setDataFetched(true);
    }
  }, [data]);

  useEffect(() => {
    // console.log('here is the ordersData in OrdersContextProvider: ', ordersData);
  }, [ordersData]);

  useEffect(() => {
    // console.log('here is the mealOptionsData in OrdersContextProvider: ', mealOptionsData);
  }, [mealOptionsData]);

  const handleSetOrderManagementTab = useCallback((newTab) => {
    setOrderManagementTab(newTab);
  }, []);

  const updateOrderMeals = useCallback(
    async (orderId, newMeals) => {
      if (!ordersData) return;

      try {
        const response = await updateOrder(orderId, newMeals.location_id, newMeals);
        if (response.success) {
          const updateOrderList = (orders) => {
            return orders.map((order) => (order.id === orderId ? { ...order, order_items: newMeals } : order));
          };

          const updatedOrdersData = updateOrderList(ordersData);
          setOrdersData(updatedOrdersData);
          enqueueSnackbar('Order updated successfully.', { variant: 'success' });
        } else {
          enqueueSnackbar(`Failed to update order: ${response.error}`, { variant: 'error' });
        }
      } catch (error) {
        enqueueSnackbar('An unexpected error occurred.', { variant: 'error' });
      }
    },
    [ordersData, enqueueSnackbar],
  );

  const updateOrderAddress = useCallback(
    async (orderId, newAddressId) => {
      if (!ordersData) return { success: false };

      const currentOrder = ordersData.find((order) => order.id === orderId);
      const currentOrderItems = currentOrder ? currentOrder.order_items : [];

      try {
        const response = await updateOrder(orderId, newAddressId, currentOrderItems);
        if (response.success) {
          enqueueSnackbar('Order updated successfully.', { variant: 'success' });
          refetchOrders();
          return { success: true };
        } else {
          enqueueSnackbar('Something went wrong, try again.', { variant: 'error' });
          return { success: false };
        }
      } catch (error) {
        enqueueSnackbar('An unexpected error occurred.', { variant: 'error' });
        return { success: false };
      }
    },
    [ordersData, refetchOrders],
  );

  const deleteThisOrder = useCallback(
    (orderId) => {
      return new Promise((resolve, reject) => {
        deleteOrder(orderId)
          .then(() => {
            return refetchOrders();
          })
          .then(() => {
            resolve(true);
          })
          .catch((error) => {
            console.error('Error deleting order:', error);
            reject(false);
          });
      });
    },
    [ordersData, refetchOrders],
  );

  const updateNewAddress = useCallback(
    (address) => {
      if (!address) return null;

      return putNewAddress(address)
        .then((response) => {
          if (response) {
            refetchUserAddress();
            return response;
          } else {
            console.error('Failed to update address');
            return null;
          }
        })
        .catch((error) => {
          console.error('Error updating new address:', error);
          return null;
        });
    },
    [ordersData, refetchOrders, refetchUserAddress],
  );

  const updateMealQuantity = (orderId, mealId, newQuantity) => {
    setOrdersData((prevData) => {
      return prevData.map((order) => {
        if (order.id === orderId) {
          const updatedOrderItems = order.order_items.map((item) =>
            item.box_id === mealId ? { ...item, quantity: newQuantity } : item,
          );
          return { ...order, order_items: updatedOrderItems };
        }
        return order;
      });
    });
  };

  const contextValue = {
    actions: {
      setOrderManagementTab: handleSetOrderManagementTab,
      updateOrderMeals,
      updateMealQuantity,
      updateOrderAddress,
      updateNewAddress,
      adjustTag,
      deleteThisOrder,
    },
    state: {
      orderManagementTab,
      ordersData,
      mealOptionsData,
      isLoading,
      dataFetched,
      mappedOrdersData,
      ordersMetrics,
    },
  };

  return <OrdersContext.Provider value={contextValue}>{children}</OrdersContext.Provider>;
};
